import type { ViewMode } from "@app/components/file_explorer/FileExplorerItem";
import type { FileExplorerSortMode } from "@app/components/file_explorer/types";
import { useIsMobile } from "@app/lib/swr/useIsMobile";
import {
  ArrowDown,
  ArrowUp,
  Button,
  CheckDone01,
  Clock,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  List,
  SearchInput,
} from "@dust-tt/sparkle";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

const SORT_ITEMS: Record<
  FileExplorerSortMode,
  {
    labelKey: string;
    defaultLabel: string;
    icon: React.ComponentType<{ className?: string }>;
  }
> = {
  "last-modified": {
    labelKey: "pods.fileExplorerSortLastModified",
    defaultLabel: "Last modified",
    icon: Clock,
  },
  "name-asc": {
    labelKey: "pods.fileExplorerSortNameAsc",
    defaultLabel: "Name A → Z",
    icon: ArrowDown,
  },
  "name-desc": {
    labelKey: "pods.fileExplorerSortNameDesc",
    defaultLabel: "Name Z → A",
    icon: ArrowUp,
  },
};

interface ViewToggleProps {
  value: ViewMode;
  onValueChange: (v: ViewMode) => void;
}

function ViewToggle({ value, onValueChange }: ViewToggleProps) {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          icon={value === "grid" ? List : CheckDone01}
          isSelect
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem
          label={t("pods.fileExplorerViewGrid", "Grid")}
          onClick={() => onValueChange("grid")}
        />
        <DropdownMenuItem
          label={t("pods.fileExplorerViewList", "List")}
          onClick={() => onValueChange("list")}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface SortDropdownProps {
  value: FileExplorerSortMode;
  onValueChange: (v: FileExplorerSortMode) => void;
}

function SortDropdown({ value, onValueChange }: SortDropdownProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const current = SORT_ITEMS[value];
  const currentLabel = t(current.labelKey, current.defaultLabel);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          icon={current.icon}
          label={isMobile ? undefined : currentLabel}
          tooltip={isMobile ? currentLabel : undefined}
          isSelect
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {(Object.keys(SORT_ITEMS) as FileExplorerSortMode[]).map((mode) => {
          const item = SORT_ITEMS[mode];
          return (
            <DropdownMenuItem
              key={mode}
              icon={item.icon}
              label={t(item.labelKey, item.defaultLabel)}
              onClick={() => onValueChange(mode)}
            />
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface FileExplorerToolbarProps {
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  viewMode: ViewMode;
  onViewModeChange: (v: ViewMode) => void;
  sortMode: FileExplorerSortMode;
  onSortModeChange: (v: FileExplorerSortMode) => void;
  toolbarExtraActions?: ReactNode;
}

export function FileExplorerToolbar({
  searchQuery,
  onSearchQueryChange,
  viewMode,
  onViewModeChange,
  sortMode,
  onSortModeChange,
  toolbarExtraActions,
}: FileExplorerToolbarProps) {
  const { t } = useTranslation();
  return (
    <div className="flex shrink-0 items-center gap-2">
      <SearchInput
        name="file-explorer-search"
        placeholder={t("pods.fileExplorerSearchPlaceholder", "Search files")}
        value={searchQuery}
        onChange={onSearchQueryChange}
        className="flex-1"
      />
      <ViewToggle value={viewMode} onValueChange={onViewModeChange} />
      <SortDropdown value={sortMode} onValueChange={onSortModeChange} />
      {toolbarExtraActions}
    </div>
  );
}
